import classNames from "classnames/bind";
import { BackgroundColorProperty } from "csstype";
import { uniqueId } from "lodash";
import * as React from "react";
import { Component, PropsWithChildren } from "react";
import { Keys } from "~/common/enums";
import { Flex } from "~/sharedComponents/layout";
import { colorOfContentMap, whiteHexColor } from "~/sharedHOC/colorPicker/const";
import { blockForFullStory, BlockForFullStory } from "~/utils/blockForFullStory";
import { TipIcon } from "../tooltip/TipIcon";
import * as styles from "./Checkbox.less";
import { CheckboxRadioGroupContext } from "./CheckboxRadioGroup";

const classNamesCtx = classNames.bind(styles);

export type CheckboxOnChange = (newValue: boolean, name?: string) => void;

export interface CommonCheckboxProps {
    checked: boolean;
    disabled?: boolean;
    error?: boolean;
    tabIndex?: number;
    focused?: boolean;
    onChange?: CheckboxOnChange;
    onClick?: (event: React.MouseEvent) => void;
    className?: string;
    color?: BackgroundColorProperty;
    textError?: boolean;
    indeterminate?: boolean;
    description?: string | JSX.Element;
    withHierarchy?: boolean;
    hierarchyPart?: string;
}

export enum checkboxTheme {
    /**
     * Checkbox with single-line text truncated with ellipsis.
     */
    ellipsis = "ellipsis",
    nowrap = "nowrap",
    pureSize = "pure_size",
    // ^ only pure size of checkbox itself (line height reset, pure checkbox square size)
    vCentered = "v_centered",
    /**
     * Used for list of checkboxes.
     */
    list = "list",
    message_board = "message_board",
    big_color = "big_color",
    /**
     * Checkbox with multiline text and default line-height.
     */
    multiline = "multiline",
    only_checkbox = "only_checkbox",
}

interface ThemedCheckboxProps<T> extends CommonCheckboxProps {
    theme?: T | T[];
}

export type CheckboxProps = ThemedCheckboxProps<checkboxTheme> &
    BlockForFullStory & {
        name?: string;
        title?: string;
        testId?: string;
    };

interface CheckboxEventTarget extends EventTarget {
    tagName?: string;
}

interface CheckboxMouseEvent extends React.MouseEvent<HTMLElement> {
    target: CheckboxEventTarget;
}

interface CheckboxKeyboardEvent extends React.KeyboardEvent<HTMLElement> {
    target: CheckboxEventTarget;
}

const defaultProps = {
    focused: true,
};

// fixing react-redux connect problem
type Props = PropsWithChildren<CheckboxProps>;
type State = {
    labelId?: string;
};

export class Checkbox extends Component<Props, State> {
    static defaultProps = defaultProps;

    constructor(props: CheckboxProps) {
        super(props);
        this.handlerKeyDown = this.handlerKeyDown.bind(this);
        this.toggleChange = this.toggleChange.bind(this);
        this.onClick = this.onClick.bind(this);

        this.state = {
            labelId: this.props.children ? uniqueId("ch_l") : undefined,
        };
    }

    onClick(event: React.MouseEvent) {
        this.props.onClick?.(event);
        if (!event.isDefaultPrevented()) {
            return this.toggleChange();
        }
    }

    toggleChange(event?: CheckboxMouseEvent) {
        if (event && event.target.tagName.toLocaleLowerCase() === "a") {
            return;
        }

        if (this.props.disabled) {
            return;
        }
        const newValue = !this.props.checked;
        return this.props.onChange && this.props.onChange(newValue, this.props.name);
    }

    handlerKeyDown(event: CheckboxKeyboardEvent) {
        if (event && event.target.tagName.toLocaleLowerCase() === "a") {
            return;
        }

        switch (event.key) {
            case Keys.Space:
                this.toggleChange();
                event.preventDefault();
                break;
        }
    }

    getThemeClassNames = () => {
        let { theme } = this.props;

        if (theme) {
            if (typeof theme === "string") {
                theme = [theme];
            }
            return theme.map(t => styles[t]);
        }
    };

    render() {
        const {
            checked,
            disabled,
            error,
            className,
            tabIndex,
            color,
            focused,
            textError,
            indeterminate,
            withHierarchy,
            hierarchyPart,
            testId,
        } = this.props;

        const _tabIndex = disabled || !focused ? undefined : tabIndex ? tabIndex : 0;

        return (
            <div
                className={classNamesCtx(
                    styles.wrapper,
                    withHierarchy && styles.hierarchy,
                    className,
                    this.getThemeClassNames(),
                    {
                        checked,
                        indeterminate,
                        disabled,
                        error,
                    }
                )}
                aria-checked={checked ? "true" : "false"}
                aria-disabled={disabled ? "true" : "false"}
                role={"checkbox"}
                onClick={this.onClick}
                tabIndex={_tabIndex}
                onKeyDown={this.handlerKeyDown}
                data-testid={testId ? `checkbox-${testId}` : null}
                aria-labelledby={this.state.labelId}
            >
                {withHierarchy && (
                    <Flex style={{ flexWrap: "nowrap" }} dangerouslySetInnerHTML={{ __html: hierarchyPart }} />
                )}
                <div className={styles.checkbox}>
                    {color && (
                        <div
                            style={{ backgroundColor: color }}
                            data-testid="checkbox-color"
                            className={classNamesCtx(
                                styles.color_checkbox,
                                color === whiteHexColor && styles.white_color_checkbox
                            )}
                        >
                            {checked && (
                                <svg className={styles.svg_icon}>
                                    <DefaultPolyline stroke={colorOfContentMap[color]} />
                                </svg>
                            )}
                        </div>
                    )}
                </div>
                {this.props.children && (
                    <>
                        <span
                            id={this.state.labelId}
                            className={classNamesCtx(
                                blockForFullStory(styles.text, this.props),
                                textError && styles.textError
                            )}
                            title={this.props.title}
                        >
                            {this.props.children}
                        </span>
                        {this.props.description && (
                            <Flex className={styles.description_wrapper} alignItems="center">
                                <TipIcon className={styles.description}>{this.props.description}</TipIcon>
                            </Flex>
                        )}
                    </>
                )}
            </div>
        );
    }
}

type CheckboxGroupProps = Partial<CheckboxProps> & {
    name: string;
    theme?: checkboxTheme | checkboxTheme[];
};

const DefaultPolyline: React.FC<{ stroke: string }> = ({ stroke = "currentColor" }) => (
    <polyline
        points="12 6 7 11 4 8"
        fill="none"
        stroke={stroke}
        strokeMiterlimit="10"
        strokeLinejoin="round"
        strokeWidth="2"
        fillRule="evenodd"
    />
);

export const CheckboxGroup: React.FC<CheckboxGroupProps> = props => {
    return (
        <CheckboxRadioGroupContext.Consumer>
            {({ selectedName, onChange }) => (
                <Checkbox checked={props.name === selectedName} onChange={onChange} {...props}>
                    {props.children}
                </Checkbox>
            )}
        </CheckboxRadioGroupContext.Consumer>
    );
};
